import { useEffect, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import toast from "react-hot-toast";
import { updateDepartmentColors } from "../../services/departments/updateDepartmentColors";
import {
    DEPARTMENT_COLOR_VALIDATION_MESSAGE,
    DEPARTMENT_COLOR_VALIDATION_TOAST_ID,
    hasValidDepartmentColorConfiguration,
    isValidHexColor,
} from "./departmentColorValidation";

const INVALID_HEX_TOAST_ID = "department-color-invalid-hex";
const SAVE_COLORS_ERROR_TOAST_ID = "department-colors-save-error";

function normalizeColorForInput(colorHex) {
    return typeof colorHex === "string" ? colorHex.trim() : "";
}

function normalizeColorForPayload(colorHex) {
    const normalizedColor = normalizeColorForInput(colorHex);
    return normalizedColor === "" ? null : normalizedColor;
}

export default function DepartmentColorsModal({
    departments,
    isModalOpen,
    setIsModalOpen,
    onSaved,
}) {
    const cancelButtonRef = useRef(null);
    const [draftDepartments, setDraftDepartments] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isModalOpen) return;

        setDraftDepartments(
            departments.map((department) => ({
                ...department,
                colorHex: normalizeColorForInput(department.colorHex),
            })),
        );
    }, [departments, isModalOpen]);

    const updateDepartmentColor = (departmentId, colorHex) => {
        setDraftDepartments((currentDepartments) =>
            currentDepartments.map((department) =>
                department.id === departmentId
                    ? { ...department, colorHex }
                    : department,
            ),
        );
    };

    const clearAllColors = () => {
        setDraftDepartments((currentDepartments) =>
            currentDepartments.map((department) => ({
                ...department,
                colorHex: "",
            })),
        );
    };

    const closeModal = () => {
        if (isSaving) return;
        setIsModalOpen(false);
    };

    const saveColors = async () => {
        if (!hasValidDepartmentColorConfiguration(draftDepartments)) {
            toast.error(DEPARTMENT_COLOR_VALIDATION_MESSAGE, {
                id: DEPARTMENT_COLOR_VALIDATION_TOAST_ID,
            });
            return;
        }

        const hasInvalidColor = draftDepartments.some(
            (department) =>
                department.colorHex.trim() !== "" &&
                !isValidHexColor(department.colorHex.trim()),
        );

        if (hasInvalidColor) {
            toast.error("Invalid HEX. Use #RRGGBB or #RRGGBBAA.", {
                id: INVALID_HEX_TOAST_ID,
            });
            return;
        }

        setIsSaving(true);

        try {
            const response = await updateDepartmentColors(
                draftDepartments.map((department) => ({
                    departmentId: department.id,
                    colorHex: normalizeColorForPayload(department.colorHex),
                })),
            );

            if (!response.success) {
                toast.error(response.message || "Failed to save colors.", {
                    id:
                        response.message === DEPARTMENT_COLOR_VALIDATION_MESSAGE
                            ? DEPARTMENT_COLOR_VALIDATION_TOAST_ID
                            : SAVE_COLORS_ERROR_TOAST_ID,
                });
                return;
            }

            toast.success("Department colors saved successfully.");
            setIsModalOpen(false);
            onSaved();
        } catch (error) {
            toast.error(error.message || "Failed to save colors.", {
                id: SAVE_COLORS_ERROR_TOAST_ID,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {isModalOpen && (
                <Dialog
                    initialFocus={cancelButtonRef}
                    static
                    as={motion.div}
                    open={isModalOpen}
                    onClose={closeModal}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:p-6">
                                <div className="flex justify-between w-full">
                                    <Dialog.Title
                                        as="h3"
                                        className="w-full text-lg font-medium leading-6 text-left text-gray-900"
                                    >
                                        Manage Department Colors
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        disabled={isSaving}
                                        className="text-slate-500 hover:text-slate-700 disabled:opacity-50"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mt-5 border rounded-sm border-slate-200">
                                    <div className="hidden md:grid md:grid-cols-[1fr_120px_230px] gap-3 px-4 py-2 text-xs font-semibold uppercase bg-slate-50 text-slate-500">
                                        <span>Department</span>
                                        <span>Current color</span>
                                        <span>Color</span>
                                    </div>

                                    <div className="overflow-y-auto divide-y max-h-[55vh] divide-slate-200">
                                        {draftDepartments.map((department) => {
                                            const colorHex =
                                                department.colorHex.trim();
                                            const hasValidColor =
                                                colorHex !== "" &&
                                                isValidHexColor(colorHex);

                                            return (
                                                <div
                                                    key={department.id}
                                                    className="grid gap-3 px-4 py-3 md:grid-cols-[1fr_120px_230px] md:items-center"
                                                >
                                                    <div className="text-sm font-medium text-slate-800">
                                                        {department.name}
                                                    </div>

                                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                                        <span
                                                            className="block w-6 h-6 border rounded-sm border-slate-200"
                                                            style={{
                                                                backgroundColor:
                                                                    hasValidColor
                                                                        ? colorHex
                                                                        : "#ffffff",
                                                            }}
                                                        />
                                                        <span className="break-all">
                                                            {colorHex || "No color"}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center min-w-0 gap-2">
                                                        <input
                                                            type="color"
                                                            value={
                                                                hasValidColor
                                                                    ? colorHex
                                                                    : "#6B7280"
                                                            }
                                                            onChange={(event) =>
                                                                updateDepartmentColor(
                                                                    department.id,
                                                                    event.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="h-10 p-1 bg-white border rounded-sm w-14 border-slate-200"
                                                            title="Pick a color"
                                                            disabled={isSaving}
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="#AABBCC"
                                                            value={
                                                                department.colorHex
                                                            }
                                                            onChange={(event) =>
                                                                updateDepartmentColor(
                                                                    department.id,
                                                                    event.target.value.trim(),
                                                                )
                                                            }
                                                            className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                            disabled={isSaving}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="flex flex-col-reverse gap-3 mt-6 sm:flex-row sm:justify-end">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center px-4 py-2 text-sm font-medium bg-white border rounded-sm shadow-sm border-slate-200 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-0 disabled:opacity-50"
                                        onClick={closeModal}
                                        ref={cancelButtonRef}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex justify-center px-4 py-2 text-sm font-medium bg-white border rounded-sm shadow-sm border-slate-200 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-0 disabled:opacity-50"
                                        onClick={clearAllColors}
                                        disabled={isSaving}
                                    >
                                        Clear all colors
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:opacity-50"
                                        onClick={saveColors}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? "Saving..." : "Save colors"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
}
